import React, { useRef } from 'react';
import { X, Clock, Play, Sparkles, ImagePlus } from 'lucide-react';
import { format } from 'date-fns';
import { PlatformSelector } from './PlatformSelector';
import { ImageSelector } from './ImageSelector';
import { ScheduleSelector } from './ScheduleSelector';
import { WebhookConfig } from './WebhookConfig';
import { ImageCropModal } from './ImageCropModal';
import { TaskFormGuide } from './TaskFormGuide';
import { InfoTooltip } from './InfoTooltip';
import { AIContentAssistant } from './AIContentAssistant';
import { CustomContentModal } from './CustomContentModal';
import { Edit3 } from 'lucide-react';
import type { TaskFormProps } from './types';
import { useTaskForm } from '../../hooks/useTaskForm';

export const TaskForm: React.FC<TaskFormProps> = ({
  onClose,
  onSubmit,
  editingTask,
}) => {
  const {
    content,
    setContent,
    selectedPlatform,
    selectedAccount,
    selectedDate,
    selectedTime,
    selectedTimezone,
    userDefaultTimezone,
    cycle,
    selectedDays,
    customWebhook,
    imagePreview,
    generateImage,
    modifyImage,
    fullModifyImage,
    isAiGenerated,
    connectedAccounts,
    isLoading,
    isTesting,
    isUploading,
    minTime,
    showWebhookInfo,
    imageToCrop,
    showCropModal,
    showAIAssistant,
    showCustomContentModal,
    generateText,
    setGenerateText,
    logoUrl,
    handleLogoUpload,
    handleLogoRemove,
    handlePlatformSelect,
    handleAccountSelect,
    handleImageChange,
    handleCropComplete,
    handleCropCancel,
    handleGenerateImageToggle,
    handleModifyImageToggle,
    handleFullModifyImageToggle,
    handleAiGeneratedToggle,
    handleImageRemove,
    handleDateChange,
    handleTimeChange,
    handleTimezoneChange,
    handleCycleChange,
    handleDaysChange,
    handleWebhookChange,
    handleWebhookInfoToggle,
    handleAIAssistantToggle,
    handleCustomContentModalToggle,
    handleAIContentGenerated,
    handleCustomContentSave,
    handleTestWebhook,
    handleSubmit,
  } = useTaskForm({
    onClose,
    onSubmit,
    editingTask,
  });

  const logoInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <div className="h-full flex flex-col bg-white">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
        <h2 className="text-lg font-semibold">
          {editingTask ? 'Edit Task' : 'Schedule New Task'}
        </h2>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-full"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Form content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-6">
          <PlatformSelector
            selectedPlatform={selectedPlatform}
            selectedAccount={selectedAccount}
            connectedAccounts={connectedAccounts}
            onPlatformSelect={handlePlatformSelect}
            onAccountSelect={handleAccountSelect}
          />

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                Content {generateText ? '(AI Prompt)' : '(Exact Text)'}
                <InfoTooltip
                  text={generateText ? "Napisz temat lub ogólny zarys, a sztuczna inteligencja przygotuje post." : "Ten tekst zostanie opublikowany dokładnie tak, jak go wpiszesz."}
                  position="bottom"
                />
              </label>
              <div className="flex gap-2">
                <button
                  onClick={handleCustomContentModalToggle}
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg
                    focus:outline-none focus:ring-2 focus:ring-offset-2
                    transition-all shadow-sm hover:shadow-md
                    ${!generateText
                      ? 'text-white bg-purple-600 hover:bg-purple-700 focus:ring-purple-500 ring-2 ring-purple-300'
                      : 'text-gray-700 bg-gray-100 hover:bg-gray-200 focus:ring-gray-500 border border-gray-200'
                    }`}
                >
                  <Edit3 className="w-4 h-4" />
                  <span>Własny opis</span>
                </button>
                <button
                  onClick={handleAIAssistantToggle}
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg
                    focus:outline-none focus:ring-2 focus:ring-offset-2
                    transition-all shadow-sm hover:shadow-md
                    ${generateText
                      ? 'text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:ring-blue-500 ring-2 ring-blue-300'
                      : 'text-gray-700 bg-gray-100 hover:bg-gray-200 focus:ring-gray-500 border border-gray-200'
                    }`}
                >
                  <Sparkles className="w-4 h-4" />
                  <span>AI Assistant</span>
                </button>
              </div>
            </div>
            <textarea
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                // If they start typing in the box and it's currently set to exact text, maybe we keep it exact.
                // If they want it to be a prompt, they can click "AI Assistant" or we can just leave generateText alone.
              }}
              className={`w-full h-32 p-3 border rounded-lg focus:ring-2 resize-none transition-colors ${
                generateText 
                  ? 'focus:ring-blue-500 border-gray-300' 
                  : 'focus:ring-purple-500 border-purple-200 bg-purple-50'
              }`}
              placeholder={generateText ? "What would you like to share? (AI will enhance this)" : "Enter your exact post content here..."}
            />
            

            {generateText && (
              <p className="mt-2 text-sm text-gray-500">
                Use ** for bold, * for italic, __ for underline
              </p>
            )}
          </div>

          <ImageSelector
            imagePreview={imagePreview}
            generateImage={generateImage}
            modifyImage={modifyImage}
            fullModifyImage={fullModifyImage}
            isUploading={isUploading}
            selectedPlatform={selectedPlatform}
            onImageChange={handleImageChange}
            onGenerateImageToggle={handleGenerateImageToggle}
            onModifyImageToggle={handleModifyImageToggle}
            onFullModifyImageToggle={handleFullModifyImageToggle}
            onImageRemove={handleImageRemove}
          />

          {/* Logo Overlay Section */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                <ImagePlus className="w-4 h-4" />
                <span>Logotyp (nakładka na zdjęcie)</span>
              </label>
            </div>
            {logoUrl ? (
              <div className="flex items-center gap-3">
                <div className="relative w-16 h-16 border rounded-lg overflow-hidden bg-gray-50">
                  <img src={logoUrl} alt="Logo" className="w-full h-full object-contain p-1" />
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-xs text-green-600 font-medium">Logo dodane ✓</p>
                  <button
                    type="button"
                    onClick={handleLogoRemove}
                    disabled={isUploading}
                    className="text-xs text-red-500 hover:text-red-700 underline"
                  >
                    Usuń logo
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                disabled={isUploading}
                className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg hover:bg-gray-50 text-sm text-gray-600"
              >
                <ImagePlus className="w-4 h-4" />
                <span>Dodaj logo</span>
              </button>
            )}
            <input
              type="file"
              className="hidden"
              ref={logoInputRef}
              accept="image/*"
              onChange={handleLogoUpload}
            />
            <p className="mt-2 text-xs text-gray-500">
              Logo zostanie nałożone na zdjęcie posta w lewym górnym rogu.
            </p>
          </div>

          {(selectedPlatform === 'instagram' || selectedPlatform === 'instagram_direct') && (
            <div className="border rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={isAiGenerated}
                  onChange={handleAiGeneratedToggle}
                  className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">
                      Mark as AI-generated
                    </span>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                      Instagram
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Instagram will display "Made with AI" label on this post. Required by Meta for AI-generated content.
                  </p>
                </div>
              </label>
            </div>
          )}

          <ScheduleSelector
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            selectedTimezone={selectedTimezone}
            userDefaultTimezone={userDefaultTimezone}
            cycle={cycle}
            selectedDays={selectedDays}
            minTime={minTime}
            onDateChange={handleDateChange}
            onTimeChange={handleTimeChange}
            onTimezoneChange={handleTimezoneChange}
            onCycleChange={handleCycleChange}
            onDaysChange={handleDaysChange}
          />

          <WebhookConfig
            webhookUrl={customWebhook}
            showInfo={showWebhookInfo}
            onWebhookChange={handleWebhookChange}
            onToggleInfo={handleWebhookInfoToggle}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t bg-gray-50 sticky bottom-0">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2 text-gray-600 text-sm overflow-hidden">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">
              First run: {format(selectedDate, 'PPP')} at {selectedTime} ({selectedTimezone})
            </span>
          </div>
          <div className="flex gap-2">
            {editingTask && (
              <button
                onClick={handleTestWebhook}
                disabled={isTesting || isUploading || !content || !selectedAccount}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 
                  focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2
                  disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              >
                <Play className="w-4 h-4" />
                <span>{isTesting ? 'Testing...' : 'Test Now'}</span>
              </button>
            )}
            <button
              onClick={handleSubmit}
              disabled={isLoading || isUploading || !content || !selectedAccount}
              className="flex-1 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving...' : editingTask ? 'Update Task' : 'Schedule Task'}
            </button>
          </div>
        </div>
      </div>

      </div>

      {/* Image Crop Modal - rendered outside main container */}
      {showCropModal && imageToCrop && (
        <ImageCropModal
          imageFile={imageToCrop}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}

      {/* AI Content Assistant Modal */}
      {showAIAssistant && (
        <AIContentAssistant
          onClose={handleAIAssistantToggle}
          onContentGenerated={handleAIContentGenerated}
          contentType="post"
        />
      )}

      {/* Custom Content Modal */}
      {showCustomContentModal && (
        <CustomContentModal
          initialContent={content}
          onClose={handleCustomContentModalToggle}
          onSave={handleCustomContentSave}
        />
      )}

      {/* Task Form Guide */}
      <TaskFormGuide />
    </>
  );
};